import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ClassNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import Accordion from "../../components/Accordion";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import CheckPointSummary from "../../components/CheckPointSummary";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import ReviewDateSummary from "../../components/ReviewDateSummary";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionBasicInfo from "../../components/SessionBasicInfo";
import SessionDeleteButton from "../../components/SessionDeleteButton";
import SessionSummary from "../../components/SessionSummary";
import Stepper from "../../components/Stepper";
import SubjectListWithTarget from "../../components/SubjectListWithTarget";
import { DeleteSessionStatus, SessionStatus } from "../../config/Config";
import { sessionPath } from "../../config/GetPagePath";
import {
  IsCampaignManager,
  IsSessionCoach,
  IsSessionCoachee,
} from "../../config/Roles";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import GetSessionData from "../../services/getSessionData";
import GetReportingManagerForSession from "../../services/getSessionReportingManager";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { utcToLocal } from "../../utils/datetime";
import styles from "./SessionsReviewSummary.module.scss";
// Example checkpoints data structure:
//
//
// [
//   {
//     checkpoint_date: "2021-03-29 12:00:00",
//     subject_id: 68,
//     subject: "Quality ",
//     checkpoint_progress_value: null,
//     action_at_checkpoint: null,
//   },
//   {
//     checkpoint_date: "2021-03-30 12:00:00",
//     subject_id: 68,
//     subject: "Quality ",
//     checkpoint_progress_value: null,
//     action_at_checkpoint: null,
//   },
//   {
//     checkpoint_date: "2021-03-29 12:00:00",
//     subject_id: 69,
//     subject: "Productivity ",
//     checkpoint_progress_value: null,
//     action_at_checkpoint: null,
//   },
//   {
//     checkpoint_date: "2021-03-30 12:00:00",
//     subject_id: 69,
//     subject: "Productivity ",
//     checkpoint_progress_value: null,
//     action_at_checkpoint: null,
//   },
// ],

export function SessionsReviewSummary() {
  const history = useHistory();
  const { addNotification } = useNotification();
  const { id } = useParams();
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionDetails, setSessionDetails] = useState({});
  const [checkpoints, setCheckpoints] = useState([]);
  const [finalData, setFinalData] = useState([]);
  const [growFormDetails, setGrowFormDetails] = useState({});
  const [subjectList, setSubjectList] = useState([]);
  const [currentReviewDate, setCurrentReviewDate] = useState([]);
  const [previousReviewDate, setPreviousReviewDate] = useState([]);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [clockRunCheck, setClockRunCheck] = useState(false);
  const [reportingManager, setReportingManager] = useState(null);
  const [sessionCoach, setSessionCoach] = useState(null);
  const [sessionCoachee, setSessionCoachee] = useState(null);
  const [isReadyForReview, setIsReadyForReview] = useState(false);
  const [sessionSummaryText, setSessionSummaryText] = useState(null);
  const [KpiStatus, setKpiStatus] = useState(null);

  useEffect(() => {
    // get session details
    async function fetchSessionData() {
      setIsLoading(true);
      const sessionAPIData = await GetSessionData(id);
      const reportingManagerAPIData = await GetReportingManagerForSession(id);
      if (sessionAPIData) {
        setSessionDetails(sessionAPIData);
        setSubjectList(sessionAPIData.subject);
        setKpiStatus(sessionAPIData.subject[0].kpi_status);
      } else {
        addNotification(
          `Could not get session details from id #${id}`,
          "error"
        );
      }
      if (reportingManagerAPIData) {
        setReportingManager(reportingManagerAPIData);
      }
      setIsLoading(false);
    }

    fetchSessionData();

    // get checkpoint details
    apiRequest
      .get(`/api/getCheckpointDetails?session_id=${id}`)
      .then(response => {
        const data = response.data.data;
        if (data && data.progress_data) {
          setCheckpoints(data.progress_data);
        }
        if (data && data.final_data) {
          setFinalData(data.final_data);
        }
        if (data && data.summary_data) {
          setSessionSummaryText(data.summary_data.summary);
        }
      })
      .catch(error => {
        console.error(error);
      });
  }, []);
  useEffect(() => {
    setSessionCoach(IsSessionCoach(getSessionValue("coach_id")));
    setSessionCoachee(IsSessionCoachee(getSessionValue("coachee_id")));
    setIsReadyForReview(
      getSessionValue("status") === SessionStatus.READY_FOR_REVIEW
    );
    setTimeout(function () {
      setClockRunCheck(true);
    }, 500);
  }, [sessionDetails]);

  // helper function to access values within session details
  const getSessionValue = key => {
    return sessionDetails.session ? sessionDetails.session[key] : undefined;
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  const handleConfirm = () => {
    setIsLoading(true);
    // {
    //   "session_id": 0,
    //   "duration": 0,
    //   "created_by": 0
    // }
    let paramObj = {
      session_id: parseInt(id),
      duration: sessionDuration,
      created_by: sessionDetails.session.coach_id,
    };
    apiRequest
      .post(`/api/endReviewSession`, paramObj)
      .then(resp => {
        if (resp.status === 200) {
          const data = resp.data.data;
          addNotification(t("reviewSession.done"), "success");
          setIsLoading(false);
          setTimeout(() => {
            history.push(sessionPath());
          }, 2000);
        } else {
          setIsLoading(false);
        }
      })
      .catch(error => {
        addNotification(`${error}`, "error");
        setIsLoading(false);
      })
      .finally(() => { });
  };
  return (
    <DashboardLayout
      appBarContent={
        clockRunCheck && (
          <Stepper
            phase={t("header.stepper.act")}
            phaseLabel="Act"
            sessionId={id}
            clockRun={isReadyForReview}
            getTotalTimeSpent={getTotalTime}
          />
        )
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={
            sessionDetails["coach_id"] === parseInt(getLocalValue("uid"))
          }
          accessible={true}
          hasAchievedKPICompliment={true}
        />
      }
      contentClass={styles.dashboardLayout__content}
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <div className={styles.detailsContainer}>
              <BackButton text={t("common.backToSession")} />
              <SectionTitle
                append={
                  <>
                    {getLocalValue("employee_id") === reportingManager &&
                      IsCampaignManager() &&
                      DeleteSessionStatus.includes(
                        sessionDetails?.session?.status
                      ) && <SessionDeleteButton sessionId={id} />}
                  </>
                }
              >
                Review summary
              </SectionTitle>
              <div className={styles.sessionDetailsBlock}>
                <SessionBasicInfo sessionData={sessionDetails.session} />
                <div>
                  <Accordion
                    summary={t("sessionSummary.viewSessionSummary")}
                    details={<SessionSummary id={id}></SessionSummary>}
                  />

                  <ReviewDateSummary id={id} />
                </div>
              </div>
              <div className={styles.sessionUpdateCheckpointScoresBlock}>
                <CheckPointSummary id={id} />
              </div>
              <div className={styles.sessionFinalReviewBlock}>
                <div
                  className={ClassNames(
                    styles.sessionGrowFormBlock,
                    styles.borderTop
                  )}
                >
                  {finalData.length > 0 && finalData[0].status ? (
                    <div className={styles.row}>
                      <div className={styles.rowLeft}>
                        <div className={styles.labelTitle}>
                          {t("reviewSession.finalReview")}
                        </div>
                      </div>
                      <div className={styles.rowRight}>
                        <div className={styles.content}>
                          <div className={styles.dateTime}>
                            <div className={styles.date}>
                              <CalendarTodayIcon className={styles.icon} />{" "}
                              <span>
                                {utcToLocal(
                                  finalData[0].review_datetime
                                ).format("DD MMM YYYY")}
                              </span>
                            </div>
                            <div className={styles.time}>
                              <ScheduleIcon className={styles.icon} />{" "}
                              <span>
                                {utcToLocal(
                                  finalData[0].review_datetime
                                ).format("h:mm A")}
                              </span>
                            </div>
                          </div>
                          <SubjectListWithTarget
                            list={
                              subjectList && subjectList.length > 0
                                ? subjectList
                                : []
                            }
                            finalData={
                              finalData && finalData.length > 0
                                ? finalData
                                : null
                            }
                            getOverwriting
                            hasFinal
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  {sessionSummaryText && (
                    <div className={styles.row}>
                      <div className={styles.rowLeft}>
                        <div className={styles.labelTitle}>
                          {t("reviewSession.summary")}
                        </div>
                      </div>
                      <div
                        className={styles.rowRight}
                        style={{ whiteSpace: "pre-line" }}
                      >
                        {sessionSummaryText}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.confirmButtonWrapper}>
                {sessionCoach && isReadyForReview && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleConfirm}
                    isloading={isLoading ? 1 : 0}
                  >
                    {t("reviewSession.endReviewSession")}
                  </Button>
                )}
              </div>
            </div>
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
